import * as React from "react";
import { FontIcon, mergeStyles, useTheme } from "@fluentui/react";
import { LanguageContext } from "../utilities/LocalizationModule";
import {
  ThemePreference,
  useThemePreference,
} from "../theme/ThemeContext";

// Option metadata shared by both the segmented control and the menu.
interface Option {
  key: ThemePreference;
  icon: string; // Fluent icon name
  labelKey: string; // LocalizationModule key
  fallback: string; // English default if the key isn't localized yet
}

const OPTIONS: Option[] = [
  { key: "light", icon: "Sunny", labelKey: "ThemeLight", fallback: "Light" },
  { key: "dark", icon: "ClearNight", labelKey: "ThemeDark", fallback: "Dark" },
  { key: "system", icon: "System", labelKey: "ThemeSystem", fallback: "System" },
];

// Reads a localized string with an English fallback so the control works
// before the LocalizationModule keys are added. Replace with typed access
// once ThemeLight/ThemeDark/ThemeSystem/Appearance exist in every language.
const useLabels = () => {
  const L = React.useContext(LanguageContext) as Record<string, string>;
  const t = (key: string, fallback: string) => (L && L[key]) || fallback;
  return {
    t,
    appearance: t("Appearance", "Appearance"),
  };
};

/* ------------------------------------------------------------------ */
/* Segmented control — for roomy surfaces (a Settings page).           */
/* A real radiogroup: arrow-key navigation, roving tabindex, one       */
/* tab stop, aria-checked. This is the accessible core of the pattern. */
/* ------------------------------------------------------------------ */

export const ThemeToggle: React.FC = () => {
  const { preference, setPreference } = useThemePreference();
  const theme = useTheme();
  const { t, appearance } = useLabels();

  const groupClass = mergeStyles({
    display: "inline-flex",
    border: `1px solid ${theme.semanticColors.inputBorder}`,
    borderRadius: 6,
    overflow: "hidden",
    background: theme.semanticColors.bodyBackground,
  });

  const segClass = (selected: boolean) =>
    mergeStyles({
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      fontSize: 13,
      border: "none",
      cursor: "pointer",
      color: selected
        // Fixed white, not theme.palette.white: the dark ramp inverts
        // palette.white to #1f1f1f, which would render dark-on-accent.
        ? "#ffffff"
        : theme.semanticColors.bodyText,
      background: selected ? theme.palette.themePrimary : "transparent",
      selectors: {
        "&:hover": {
          background: selected
            ? theme.palette.themeDarkAlt
            : theme.semanticColors.bodyBackgroundHovered,
        },
        // Visible keyboard focus — never remove the outline.
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.themePrimary}`,
          outlineOffset: "-2px",
        },
        "& i": { fontSize: 16 },
      },
    });

  const idx = OPTIONS.findIndex((o) => o.key === preference);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next = idx;
    if (e.key === "ArrowRight" || e.key === "ArrowDown")
      next = (idx + 1) % OPTIONS.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (idx - 1 + OPTIONS.length) % OPTIONS.length;
    else return;
    e.preventDefault();
    setPreference(OPTIONS[next].key);
    // Move focus to the newly selected radio (roving tabindex).
    const el = e.currentTarget.querySelectorAll<HTMLButtonElement>(
      '[role="radio"]'
    )[next];
    el?.focus();
  };

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={appearance}
        className={groupClass}
        onKeyDown={onKeyDown}
      >
        {OPTIONS.map((o) => {
          const selected = o.key === preference;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              className={segClass(selected)}
              onClick={() => setPreference(o.key)}
            >
              <FontIcon iconName={o.icon} aria-hidden="true" />
              {t(o.labelKey, o.fallback)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeToggle;
